/*
 * WorkAndBuhin.vue の確定表示キー、確定済み判定、選択状態復元判定を切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";
import { INSPECT_TYPE } from "@/model/InspectType";

export interface BuhinConfirmEntry {
  key: any;
  values: any[];
}

export interface BuhinConfirmEntriesApplyEffectState {
  shouldApplyEntries: boolean;
  entries: BuhinConfirmEntry[];
}

export interface OsusumeBuhinConfirmEntryApplyEffectState {
  shouldApplyEntry: boolean;
  entry: BuhinConfirmEntry | null;
}

/*
 * getWorkConfirmKey を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function getWorkConfirmKey(work: any): any {
  return work && (work.seibiDetailId || work.seibiId || work.sknNisseirenCd || work.seibiNm);
}

/*
 * getOsusumeConfirmKey を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function getOsusumeConfirmKey(seibi: any): any {
  let key: any = "";
  if (seibi) {
    if (seibi.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL) {
      if (seibi.osusumeId) {
        key = "osusume_" + seibi.osusumeId;
      } else {
        key = "seibiId_" + seibi.seibiId;
      }
    } else {
      key = seibi.sknInspectSeibiDetailId;
    }
  }
  return key;
}

/*
 * buildBuhinConfirmEntries を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function buildBuhinConfirmEntries(seibiList: any[]): BuhinConfirmEntry[] {
  if (!seibiList) {
    return [];
  }
  const entries: BuhinConfirmEntry[] = [];
  seibiList
    .filter((seibi: any) => seibi.buhinSelected != STATUS_SELECT_WORK_BUHIN.Initial)
    .forEach((seibi: any) => {
      const key = getWorkConfirmKey(seibi);
      if (key) {
        entries.push({
          key,
          values: buildNormalBuhinConfirmValues(seibi.buhinList)
        });
      }
    });
  return entries;
}

/*
 * buildBuhinConfirmEntriesApplyEffectState を WorkAndBuhin.vue から分離し、
 * 確定 map へ通常作業・手入力作業の confirm entry を反映する条件と entries を固定するため。
 */
export function buildBuhinConfirmEntriesApplyEffectState(
  entries: BuhinConfirmEntry[]
): BuhinConfirmEntriesApplyEffectState {
  return {
    shouldApplyEntries: entries.length > 0,
    entries
  };
}

/*
 * buildOsusumeBuhinConfirmEntry を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function buildOsusumeBuhinConfirmEntry(
  seibi: any,
  isInit: boolean = false
): BuhinConfirmEntry | null {
  if (!seibi) {
    return null;
  }
  const key = getOsusumeConfirmKey(seibi);
  if (!key) {
    return null;
  }
  let buhinList = seibi.buhinList || [];
  if (isInit && (!seibi.isExtreme || seibi.isExtreme != 1)) {
    buhinList = buhinList.filter((buhin: any) => buhin.id);
  }
  if (seibi.sknInspectType == INSPECT_TYPE.RECOMMEND_MANUAL && seibi.osusumeBuhinList) {
    const osusumeList = seibi.osusumeBuhinList.filter((buhin: any) => buhin.id);
    buhinList = [...buhinList, ...osusumeList];
  }
  return {
    key,
    values: buildOsusumeBuhinConfirmValues(buhinList)
  };
}

/*
 * buildOsusumeBuhinConfirmEntryApplyEffectState を WorkAndBuhin.vue から分離し、
 * 確定 map へおすすめ整備の confirm entry を反映する条件と entry を固定するため。
 */
export function buildOsusumeBuhinConfirmEntryApplyEffectState(
  entry: BuhinConfirmEntry | null
): OsusumeBuhinConfirmEntryApplyEffectState {
  return {
    shouldApplyEntry: !!entry,
    entry
  };
}

/*
 * buildNormalBuhinConfirmValues を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function buildNormalBuhinConfirmValues(buhinList: BuhinDetail[] = []): any[] {
  return buhinList
    .filter((buhin: BuhinDetail) => buhin.quantity > 0 && buhin.isDelete != 1)
    .map((buhin: BuhinDetail) => buhin.buhinCd);
}

/*
 * buildOsusumeBuhinConfirmValues を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function buildOsusumeBuhinConfirmValues(buhinList: BuhinDetail[] = []): any[] {
  return buhinList
    .filter((buhin: BuhinDetail) => buhin.quantity > 0 && buhin.isDelete != 1)
    .map((buhin: BuhinDetail) => buhin.buhinOsusumeId || buhin.buhinCd);
}

/*
 * getActiveBuhinConfirmKey を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function getActiveBuhinConfirmKey(params: {
  activeSeibiId: any;
  isManualWork: boolean;
  activeWorkIndex: number;
  data: any;
}): any {
  if (params.activeSeibiId) {
    return params.activeSeibiId;
  }
  if (
    params.isManualWork &&
    params.data.manualSeibiList &&
    params.activeWorkIndex > -1 &&
    params.data.manualSeibiList[params.activeWorkIndex]
  ) {
    return getWorkConfirmKey(params.data.manualSeibiList[params.activeWorkIndex]);
  }
  if (
    params.data.seibiList &&
    params.data.seibiList.length > 0 &&
    params.activeWorkIndex > -1
  ) {
    return getOsusumeConfirmKey(params.data.seibiList[params.activeWorkIndex]);
  }
  return getOsusumeConfirmKey(params.data);
}

/*
 * isBuhinConfirmedByKey を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function isBuhinConfirmedByKey(
  listBuhinConfirm: any,
  key: any,
  buhin: BuhinDetail
): boolean {
  if (!key || !(key in listBuhinConfirm) || buhin.quantity <= 0) {
    return false;
  }
  if (!listBuhinConfirm[key]) {
    listBuhinConfirm[key] = [];
  }
  if (buhin.buhinCd && listBuhinConfirm[key].length > 0) {
    return !!listBuhinConfirm[key].find((value: any) => value == buhin.buhinCd);
  }
  if (buhin.buhinOsusumeId) {
    return !!listBuhinConfirm[key].find((value: any) => value == buhin.buhinOsusumeId);
  }
  return false;
}

/*
 * shouldResetWorkBuhinSelected を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function shouldResetWorkBuhinSelected(params: {
  hasSeibiList: boolean;
  manualBuhinLength: number;
  selectedBuhinLength: number;
}): boolean {
  return params.hasSeibiList && params.manualBuhinLength == 0 && params.selectedBuhinLength == 0;
}

export interface WorkBuhinSelectedResetState {
  shouldResetWorkBuhinSelected: boolean;
  workBuhinSelected: number | null;
}

export interface WorkBuhinSelectedResetApplyState {
  shouldResetWorkBuhinSelected: boolean;
  workBuhinSelected: number | null;
  shouldResetDataWorkBuhinSelected: boolean;
  dataWorkBuhinSelected: number | null;
}

export type WorkBuhinSelectedResetApplyEffectState =
  WorkBuhinSelectedResetApplyState;

export interface ResetSeibiConfirmActionState {
  shouldApply: boolean;
  shouldCheckWorkBuhinSelected: boolean;
  shouldClearConfirmValues: boolean;
  confirmKey: any;
  confirmValues: any[];
}

export type ResetSeibiConfirmApplyEffectState = ResetSeibiConfirmActionState;

export interface CommitBuhinConfirmActionState {
  shouldCommitWorkConfirm: boolean;
  workConfirmSource: any;
  shouldCommitDeletedManualBuhinList: boolean;
  deletedManualBuhinListSource: any[];
}

export type CommitBuhinConfirmApplyEffectState = CommitBuhinConfirmActionState;

export interface RestoreBuhinConfirmForDialogActionState {
  shouldKeepBuhinConfirm: boolean;
  seibiList: any[];
  manualSeibiList: any[];
  shouldKeepBuhinConfirmOsusume: boolean;
  osusumeData: any;
}

export type RestoreBuhinConfirmForDialogApplyEffectState =
  RestoreBuhinConfirmForDialogActionState;

export interface KeepBuhinConfirmMd15ActionState {
  shouldClearConfirmMap: boolean;
  shouldKeepBuhinConfirm: boolean;
  shouldKeepBuhinConfirmOsusume: boolean;
  seibiList: any[];
  manualSeibiList: any[];
  osusumeData: any;
}

export type KeepBuhinConfirmMd15ApplyEffectState = KeepBuhinConfirmMd15ActionState;

/*
 * resolveWorkBuhinSelectedResetState を WorkAndBuhin.vue から分離し、
 * confirm 復元と選択済み表示判定 を親側置換や単体テストで固定するため。
 */
export function resolveWorkBuhinSelectedResetState(params: {
  data: any;
  work: any;
  manualBuhinLength: number;
  selectedBuhinLength: number;
}): WorkBuhinSelectedResetState {
  const shouldReset = shouldResetWorkBuhinSelected({
    hasSeibiList: !!(params.data && params.data.seibiList),
    manualBuhinLength: params.manualBuhinLength,
    selectedBuhinLength: params.selectedBuhinLength
  });
  if (!shouldReset) {
    return {
      shouldResetWorkBuhinSelected: false,
      workBuhinSelected: null
    };
  }
  const isSeibiAllNoBuhin = isAllNoBuhin(params.data && params.data.seibiList, params.work);
  const isManualSeibiAllNoBuhin = isAllNoBuhin(
    params.data && params.data.manualSeibiList,
    params.work
  );
  return {
    shouldResetWorkBuhinSelected: true,
    workBuhinSelected: isSeibiAllNoBuhin && isManualSeibiAllNoBuhin ? 0 : null
  };
}

/*
 * buildWorkBuhinSelectedResetApplyState を WorkAndBuhin.vue から分離し、
 * 作業単位と点検項目全体の部品選択状態 reset 反映条件を親側置換や単体テストで固定するため。
 */
export function buildWorkBuhinSelectedResetApplyState(params: {
  data: any;
  work: any;
  manualBuhinLength: number;
  selectedBuhinLength: number;
}): WorkBuhinSelectedResetApplyState {
  const resetState = resolveWorkBuhinSelectedResetState(params);
  if (!resetState.shouldResetWorkBuhinSelected) {
    return {
      shouldResetWorkBuhinSelected: false,
      workBuhinSelected: null,
      shouldResetDataWorkBuhinSelected: false,
      dataWorkBuhinSelected: null
    };
  }
  return {
    shouldResetWorkBuhinSelected: true,
    workBuhinSelected: 0,
    shouldResetDataWorkBuhinSelected: resetState.workBuhinSelected != null,
    dataWorkBuhinSelected: resetState.workBuhinSelected
  };
}

/*
 * buildWorkBuhinSelectedResetApplyEffectState を WorkAndBuhin.vue から分離し、
 * 作業単位と点検項目全体の部品選択状態 reset の反映条件・値を固定するため。
 */
export function buildWorkBuhinSelectedResetApplyEffectState(
  applyState: WorkBuhinSelectedResetApplyState
): WorkBuhinSelectedResetApplyEffectState {
  return {
    shouldResetWorkBuhinSelected: applyState.shouldResetWorkBuhinSelected,
    workBuhinSelected: applyState.workBuhinSelected,
    shouldResetDataWorkBuhinSelected: applyState.shouldResetDataWorkBuhinSelected,
    dataWorkBuhinSelected: applyState.dataWorkBuhinSelected
  };
}

/*
 * buildKeepBuhinConfirmMd15ActionState を WorkAndBuhin.vue から分離し、
 * MD15 用の確定復元で何を初期化し、通常/おすすめのどちらを復元するかを固定するため。
 */
export function buildKeepBuhinConfirmMd15ActionState(params: {
  isDialog: boolean;
  data: any;
  fallbackData: any;
}): KeepBuhinConfirmMd15ActionState {
  const targetData = params.data == null ? params.fallbackData : params.data;
  const hasData = !!targetData && Object.keys(targetData).length > 0;
  const hasSeibiList = hasData && !!targetData.seibiList;
  return {
    shouldClearConfirmMap: !params.isDialog,
    shouldKeepBuhinConfirm: !params.isDialog && hasSeibiList,
    shouldKeepBuhinConfirmOsusume: !params.isDialog && hasData && !hasSeibiList,
    seibiList: hasSeibiList ? targetData.seibiList : [],
    manualSeibiList:
      hasSeibiList && targetData.manualSeibiList ? targetData.manualSeibiList : [],
    osusumeData: targetData
  };
}

/*
 * buildKeepBuhinConfirmMd15ApplyEffectState を WorkAndBuhin.vue から分離し、
 * MD15 用の confirm map clear と通常/手入力/おすすめ復元の反映条件を固定するため。
 */
export function buildKeepBuhinConfirmMd15ApplyEffectState(
  actionState: KeepBuhinConfirmMd15ActionState
): KeepBuhinConfirmMd15ApplyEffectState {
  return {
    shouldClearConfirmMap: actionState.shouldClearConfirmMap,
    shouldKeepBuhinConfirm: actionState.shouldKeepBuhinConfirm,
    shouldKeepBuhinConfirmOsusume: actionState.shouldKeepBuhinConfirmOsusume,
    seibiList: actionState.seibiList,
    manualSeibiList: actionState.manualSeibiList,
    osusumeData: actionState.osusumeData
  };
}

/*
 * buildRestoreBuhinConfirmForDialogActionState を WorkAndBuhin.vue から分離し、
 * dialog 保存時に通常作業とおすすめ整備のどちらの confirm を復元するかを固定するため。
 */
export function buildRestoreBuhinConfirmForDialogActionState(params: {
  isDialog: boolean;
  data: any;
}): RestoreBuhinConfirmForDialogActionState {
  const hasSeibiList = !!(params.data && params.data.seibiList);
  return {
    shouldKeepBuhinConfirm: params.isDialog && hasSeibiList,
    seibiList: hasSeibiList ? params.data.seibiList : [],
    manualSeibiList: hasSeibiList && params.data.manualSeibiList
      ? params.data.manualSeibiList
      : [],
    shouldKeepBuhinConfirmOsusume: params.isDialog && !hasSeibiList,
    osusumeData: params.data
  };
}

/*
 * buildRestoreBuhinConfirmForDialogApplyEffectState を WorkAndBuhin.vue から分離し、
 * dialog 保存時の通常/手入力 confirm 復元とおすすめ confirm 復元の反映条件を固定するため。
 */
export function buildRestoreBuhinConfirmForDialogApplyEffectState(
  actionState: RestoreBuhinConfirmForDialogActionState
): RestoreBuhinConfirmForDialogApplyEffectState {
  return {
    shouldKeepBuhinConfirm: actionState.shouldKeepBuhinConfirm,
    seibiList: actionState.seibiList,
    manualSeibiList: actionState.manualSeibiList,
    shouldKeepBuhinConfirmOsusume: actionState.shouldKeepBuhinConfirmOsusume,
    osusumeData: actionState.osusumeData
  };
}

/*
 * buildResetSeibiConfirmActionState を WorkAndBuhin.vue から分離し、
 * reset 確定時に confirm map を空へ戻す条件を固定するため。
 */
export function buildResetSeibiConfirmActionState(params: {
  isResetSeibi: boolean;
  work: any;
}): ResetSeibiConfirmActionState {
  if (!params.isResetSeibi) {
    return {
      shouldApply: false,
      shouldCheckWorkBuhinSelected: false,
      shouldClearConfirmValues: false,
      confirmKey: null,
      confirmValues: []
    };
  }
  const confirmKey = getWorkConfirmKey(params.work);
  return {
    shouldApply: true,
    shouldCheckWorkBuhinSelected: true,
    shouldClearConfirmValues: !!confirmKey,
    confirmKey,
    confirmValues: []
  };
}

/*
 * buildResetSeibiConfirmApplyEffectState を WorkAndBuhin.vue から分離し、
 * reset confirm の component 側副作用条件と confirm map 反映値を固定するため。
 */
export function buildResetSeibiConfirmApplyEffectState(
  actionState: ResetSeibiConfirmActionState
): ResetSeibiConfirmApplyEffectState {
  return {
    ...actionState
  };
}

/*
 * buildCommitBuhinConfirmActionState を WorkAndBuhin.vue から分離し、
 * confirm commit 時に workConfirm と dialog 削除済みリストを退避する条件を固定するため。
 */
export function buildCommitBuhinConfirmActionState(params: {
  work: any;
  isDialog: boolean;
  tmpDeletedManualBuhinBuff: any[];
}): CommitBuhinConfirmActionState {
  return {
    shouldCommitWorkConfirm: true,
    workConfirmSource: params.work,
    shouldCommitDeletedManualBuhinList: params.isDialog,
    deletedManualBuhinListSource: params.tmpDeletedManualBuhinBuff
  };
}

/*
 * buildCommitBuhinConfirmApplyEffectState を WorkAndBuhin.vue から分離し、
 * confirm commit の deepCopy 対象と実行条件を component 側で固定して適用するため。
 */
export function buildCommitBuhinConfirmApplyEffectState(
  actionState: CommitBuhinConfirmActionState
): CommitBuhinConfirmApplyEffectState {
  return {
    ...actionState
  };
}

function isAllNoBuhin(seibiList: any[] | null | undefined, currentWork: any): boolean {
  if (!seibiList) {
    return true;
  }
  return seibiList.every((seibi: any) => seibi === currentWork || seibi.buhinSelected == 0);
}
